import { useEffect, useState } from "react";
import {
  Modal,
  Backdrop,
  Fade,
  Box,
  Typography,
  Rating,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CloseIcon from "@mui/icons-material/Close";
import { modalStyles } from "./styles/deviceControlStyles";
import nettoFeedbackImage from "../assets/images/nettoFeedbackIcons.png";

const sizeStyles = {
  sm: { minWidth: 320, maxWidth: 420 },
  md: { minWidth: 400, maxWidth: 520 },
  lg: { minWidth: 520, maxWidth: 680 },
};

function resolveModalSize(modalSize) {
  if (typeof modalSize === "object" && modalSize !== null) {
    return modalSize;
  }

  return sizeStyles[modalSize] || sizeStyles.md;
}

export default function CustomerFeedbackModal({
  open,
  onClose,
  onSubmit,
  rating,
  onRatingChange,
  comment,
  onCommentChange,
  submitting = false,
  modalSize = "md",
  showComment = true,
  title = "Cleanliness Feedback",
  description = null,
  submitLabel = "Submit",
  closeLabel = "Later",
  expirationHour = 7,
  lowRatingExperienceEnabled = false,
  lowRatingThreshold = 4,
  experienceSelections = [],
  onToggleExperience,
  experienceOptions = [],
  experiencePrompt = "What best describes your experience?",
  experienceComment = "",
  onExperienceCommentChange,
  experienceCommentLabel = "Optional comment",
  savingLabel = "Saving...",
}) {
  const [timeRemaining, setTimeRemaining] = useState({ hours: 0, minutes: 0 });

  const showExperienceStep =
    lowRatingExperienceEnabled && rating > 0 && rating <= lowRatingThreshold;

  useEffect(() => {
    if (!open) return;

    const updateCountdown = () => {
      const now = new Date();
      const expiration = new Date();
      expiration.setHours(expirationHour, 0, 0, 0);

      // If past expiration time, expiration is tomorrow
      if (now > expiration) {
        expiration.setDate(expiration.getDate() + 1);
      }

      const msRemaining = expiration - now;
      const totalMinutes = Math.ceil(msRemaining / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      setTimeRemaining({
        hours: Math.max(0, hours),
        minutes: Math.max(0, minutes),
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, [open, expirationHour]);

  const handleRatingChange = (_, nextValue) => {
    const selectedRating = nextValue || 0;
    onRatingChange(selectedRating);

    // In no-comment mode, submit immediately on star click only for high ratings.
    if (
      !showComment &&
      selectedRating >= 1 &&
      !submitting &&
      (!lowRatingExperienceEnabled || selectedRating > lowRatingThreshold)
    ) {
      onSubmit(selectedRating);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.78)",
        },
      }}
      closeAfterTransition
    >
      <Fade in={open}>
        <Box
          sx={{
            ...modalStyles.container,
            ...modalStyles.support,
            ...resolveModalSize(modalSize),
            position: "relative",
            pt: { xs: 7, sm: 7.5 },
            width: { xs: "calc(100vw - 24px)", sm: "auto" },
            minWidth: { xs: 0, sm: undefined },
            maxHeight: "90vh",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {/* Header controls */}
          <Box
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              right: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.8rem",
                color: "#666",
                fontWeight: 500,
              }}
            >
              <HourglassTopIcon sx={{ fontSize: "1rem" }} />
              <span>
                {timeRemaining.hours > 0
                  ? `${timeRemaining.hours}h ${timeRemaining.minutes}m`
                  : `${timeRemaining.minutes}m`}
              </span>
            </Box>

            <IconButton
              onClick={onClose}
              aria-label="Close feedback modal"
              size="small"
              sx={{ color: "#666" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography
            variant="h5"
            gutterBottom
            sx={{
              textAlign: "center",
              mb: 2,
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          {description && (
            <Typography
              variant="body1"
              gutterBottom
              sx={{ mb: 2, textAlign: "center" }}
            >
              {description}
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Rating
              name="customer-cleanliness-rating"
              value={rating}
              onChange={handleRatingChange}
              max={5}
              size="large"
              sx={{
                "& .MuiRating-icon": {
                  fontSize: { xs: "2.6rem", sm: "4rem" },
                },
              }}
            />
          </Box>

          {showExperienceStep ? (
            <>
              <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                {experiencePrompt}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                  mb: 3,
                }}
              >
                {experienceOptions.map((option) => {
                  const selected = experienceSelections.includes(option.value);

                  return (
                    <Button
                      key={option.value}
                      type="button"
                      onClick={() => onToggleExperience?.(option.value)}
                      variant="text"
                      sx={{
                        p: 0.5,
                        minWidth: 0,
                        borderRadius: 2,
                        border: "none",
                        backgroundColor: "transparent",
                        textTransform: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 84, sm: 100 },
                            height: { xs: 84, sm: 100 },
                            borderRadius: "50%",
                            overflow: "hidden",
                            border: selected
                              ? "3px solid"
                              : "2px solid rgba(0, 0, 0, 0.14)",
                            borderColor: selected
                              ? "primary.main"
                              : "transparent",
                            backgroundImage: `url(${nettoFeedbackImage})`,
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "303% 203%",
                            backgroundPosition: option.backgroundPosition,
                            filter: selected ? "none" : "saturate(0.95)",
                            boxShadow: selected
                              ? "0 0 0 4px rgba(25, 118, 210, 0.18)"
                              : "none",
                            transition:
                              "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                            transform: selected ? "scale(1.02)" : "scale(1)",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            px: 0.75,
                            fontWeight: selected ? 700 : 500,
                            color: "text.primary",
                            textAlign: "center",
                            lineHeight: 1.2,
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    </Button>
                  );
                })}
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={2}
                label={experienceCommentLabel}
                value={experienceComment}
                onChange={(e) => onExperienceCommentChange?.(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={onClose}
                  disabled={submitting}
                  sx={{ minWidth: 100 }}
                >
                  {closeLabel}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => onSubmit(rating)}
                  disabled={submitting || experienceSelections.length === 0}
                  sx={{ minWidth: 100 }}
                >
                  {submitting ? savingLabel : submitLabel}
                </Button>
              </Box>
            </>
          ) : showComment ? (
            <>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Optional comment"
                value={comment}
                onChange={(e) => onCommentChange(e.target.value)}
                sx={{ mb: 3 }}
              />
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={onClose}
                  disabled={submitting}
                  sx={{ minWidth: 100 }}
                >
                  {closeLabel}
                </Button>
                <Button
                  variant="contained"
                  onClick={onSubmit}
                  disabled={submitting || rating < 1}
                  sx={{ minWidth: 100 }}
                >
                  {submitting ? savingLabel : submitLabel}
                </Button>
              </Box>
            </>
          ) : null}
        </Box>
      </Fade>
    </Modal>
  );
}
